import type { DepositConfig, HallidayPaymentsConfig, WithdrawalConfig } from "@halliday-sdk/payments";
import type { ProviderConfig } from "./types";

/**
 * Deep-diff two snapshots of the Provider's **serializable** config props and emit the minimal nested
 * partial to feed `updateConfig`. Used by the Provider's reactive effect so a declarative prop
 * change (e.g. `customStyles`) propagates to the live widget.
 *
 * Rules:
 * - Emit a key only when it actually changed (deep value inequality).
 * - A key present in `prev` but absent in `next` is a **clear** → emit explicit `null` (carried through by
 *   `normalizeConfig`).
 * - Recurse one level into the `deposit`/`withdrawal` groups (their serializable sub-keys).
 * - **Exclude** functions (`onConnectWallet`) and the construction-only knobs (`instanceId`/`autoPreload`):
 *   the host-connect hook rides a stable proxy (the Provider's `connectRef`), and the id/warm flags are
 *   fixed for the instance's lifetime.
 */

// Anchored to the config type: a bogus/removed key fails typecheck here at the `satisfies`.
const TOP_LEVEL_KEYS = ["apiKey", "sandbox", "customStyles", "fontName", "headerTitle", "dangerousOverrides"] as const satisfies readonly (keyof HallidayPaymentsConfig)[];
// Anchored to the INTERSECTION because the single `diffGroup` helper runs this one list against BOTH
// `deposit` and `withdrawal`; `keyof DepositConfig` alone would wrongly permit `funders` (deposit-only).
const GROUP_KEYS = ["inputs", "outputs", "destinationAddress"] as const satisfies readonly (keyof DepositConfig &
    keyof WithdrawalConfig)[];

/** Deep equality for serializable values (functions are never compared — they are excluded upstream). */
const equal = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A nested group snapshot (`deposit`/`withdrawal`) keyed by the shared group keys. `ProviderConfig`'s
 *  `Omit<…>` group shapes are assignable to this, so the call sites need no cast. */
type GroupSnapshot = Partial<Record<(typeof GROUP_KEYS)[number], unknown>>;

/** Diff one nested group (`deposit`/`withdrawal`), returning a sparse partial or null when no change. */
const diffGroup = (prev: GroupSnapshot | undefined, next: GroupSnapshot | undefined): Record<string, unknown> | null => {
    const out: Record<string, unknown> = {};
    for (const key of GROUP_KEYS) {
        const hadPrev = prev != null && key in prev;
        const hasNext = next != null && key in next;
        if (hasNext && !equal(prev?.[key], next?.[key])) {
            out[key] = next![key];
        } else if (hadPrev && !hasNext) {
            out[key] = null;
        }
    }
    return Object.keys(out).length > 0 ? out : null;
};

export const diffConfig = (prev: ProviderConfig, next: ProviderConfig): Partial<HallidayPaymentsConfig> => {
    const out: Partial<HallidayPaymentsConfig> = {};
    // The presence-based writes carry the runtime `null` clear-signal, which the optional field types
    // don't model; the KEY names are still compiler-checked via the `satisfies`-anchored lists above.
    const write = out as Record<string, unknown>;

    // The anchored top-level keys are all keys of `ProviderConfig`, so read them typed (no bag cast).
    for (const key of TOP_LEVEL_KEYS) {
        const hadPrev = key in prev;
        const hasNext = key in next;
        if (hasNext && !equal(prev[key], next[key])) {
            write[key] = next[key];
        } else if (hadPrev && !hasNext) {
            write[key] = null;
        }
    }

    // `deposit`/`withdrawal` are assignable to `GroupSnapshot`, so pass them straight through (no cast).
    const deposit = diffGroup(prev.deposit, next.deposit);
    if (deposit) write.deposit = deposit;

    const withdrawal = diffGroup(prev.withdrawal, next.withdrawal);
    if (withdrawal) write.withdrawal = withdrawal;

    // feeSponsorship: diff its serializable `profileName` sub-key exactly like a diffGroup sub-key — emit on
    // add/rename while present, null it on whole-group removal. generateGrant is a function → excluded (like
    // onConnectWallet); its presence toggle rides the Provider's effect independently.
    const hadName = prev.feeSponsorship != null && "profileName" in prev.feeSponsorship;
    const hasName = next.feeSponsorship != null && "profileName" in next.feeSponsorship;
    if (hasName && !equal(prev.feeSponsorship?.profileName, next.feeSponsorship?.profileName)) {
        write.feeSponsorship = { profileName: next.feeSponsorship!.profileName };
    } else if (hadName && !hasName) {
        write.feeSponsorship = { profileName: null };
    }

    return out;
};
